/* ENDOINFRAX : comportements des sections ex-*.
   - <ex-thermal-viewer> : comparateur vue normale / vue thermique (photos, ou scène simulée sans photo)
   - <ex-reso-sim>       : simulateur « résolution native vs lissage logiciel »
   - <ex-use-filter>     : tuiles « Quel est votre besoin ? » qui filtrent la grille produits de la page */

/* ---------- Scène simulée : mur, fenêtre, radiateur, prise qui chauffe, zone humide ---------- */
const sd = (u, v, x0, y0, x1, y1) => Math.hypot(Math.max(x0 - u, 0, u - x1), Math.max(y0 - v, 0, v - y1));
const inR = (u, v, x0, y0, x1, y1) => u >= x0 && u <= x1 && v >= y0 && v <= y1;
const WIN = [0.12, 0.12, 0.46, 0.55];
const RAD = [0.14, 0.66, 0.44, 0.8];
const OUT = [0.6, 0.74, 0.645, 0.8];

function field(u, v) {
  let t = 21 - 1.1 * v;
  t -= 3.6 * Math.exp(-v / 0.03);
  t -= 3.2 * Math.exp(-Math.abs(u - 0.78) / 0.014);
  t -= 2.6 * Math.exp(-(1 - v) / 0.045);
  const dw = sd(u, v, ...WIN);
  if (inR(u, v, ...WIN)) {
    const edge = Math.min(u - WIN[0], WIN[2] - u, v - WIN[1], WIN[3] - v);
    t = edge < 0.018 ? 12.5 + edge * 60 : 8.6 + 1.4 * v + 0.6 * Math.sin(u * 40);
    if (u > 0.285 && u < 0.295) t = 12.8;
  } else t -= 5.5 * Math.exp(-dw / 0.022);
  const dr = sd(u, v, ...RAD);
  if (inR(u, v, ...RAD)) t = 49 + 3 * Math.cos(u * 180) - (6 * (v - RAD[1])) / (RAD[3] - RAD[1]);
  else {
    t += 24 * Math.exp(-dr / 0.018);
    if (v < RAD[1] && u > RAD[0] - 0.03 && u < RAD[2] + 0.03) t += 9 * Math.exp(-(RAD[1] - v) / 0.07);
  }
  const dout = sd(u, v, ...OUT);
  t += inR(u, v, ...OUT) ? 26 : 21 * Math.exp(-dout / 0.014);
  t -= 4.2 * Math.exp(-((u - 0.88) ** 2 + (v - 0.6) ** 2) / 0.0035);
  return t;
}

const IB = [
  [0, [16, 6, 40]],
  [0.14, [44, 10, 104]],
  [0.32, [118, 16, 148]],
  [0.5, [196, 42, 99]],
  [0.66, [238, 106, 30]],
  [0.82, [255, 186, 52]],
  [1, [255, 246, 206]],
];

function ironbow(x) {
  x = Math.min(1, Math.max(0, x));
  for (let i = 1; i < IB.length; i++) {
    if (x <= IB[i][0]) {
      const [a, ca] = IB[i - 1];
      const [b, cb] = IB[i];
      const k = (x - a) / (b - a);
      return ca.map((c, j) => Math.round(c + (cb[j] - c) * k));
    }
  }
  return IB[IB.length - 1][1];
}

const TMIN = 8;
const TMAX = 50;
const sensorCache = {};

function sensor(w, h) {
  const key = `${w}x${h}`;
  if (sensorCache[key]) return sensorCache[key];
  const c = document.createElement('canvas');
  c.width = w;
  c.height = h;
  const g = c.getContext('2d');
  const img = g.createImageData(w, h);
  let mn = 1e9;
  let mx = -1e9;
  for (let y = 0; y < h; y++) {
    for (let x = 0; x < w; x++) {
      let u = (x + 0.5) / w;
      const v = (y + 0.5) / h;
      if (w === h) u = 0.125 + u * 0.75; // capteur carré : cadré au centre de la scène 4:3
      const t = field(u, v);
      const col = ironbow((t - TMIN) / (TMAX - TMIN));
      const i = (y * w + x) * 4;
      img.data[i] = col[0];
      img.data[i + 1] = col[1];
      img.data[i + 2] = col[2];
      img.data[i + 3] = 255;
      if (t < mn) mn = t;
      if (t > mx) mx = t;
    }
  }
  g.putImageData(img, 0, 0);
  return (sensorCache[key] = { c, mn, mx });
}

function fit(canvas) {
  const r = canvas.getBoundingClientRect();
  const d = Math.min(window.devicePixelRatio || 1, 2);
  canvas.width = Math.max(1, Math.round(r.width * d));
  canvas.height = Math.max(1, Math.round(r.height * d));
  return [canvas.width, canvas.height, d];
}

const fmt = (t) => `${t.toFixed(1).replace('.', ',')} °C`;

function drawThermal(canvas, { w = 256, h = 192, smooth = true, hud = true } = {}) {
  const [W, H, d] = fit(canvas);
  const g = canvas.getContext('2d');
  const s = sensor(w, h);
  g.imageSmoothingEnabled = smooth;
  g.imageSmoothingQuality = 'high';
  const sq = w === h;
  if (sq) {
    g.fillStyle = '#0b0b10';
    g.fillRect(0, 0, W, H);
    g.drawImage(s.c, (W - H) / 2, 0, H, H);
  } else g.drawImage(s.c, 0, 0, W, H);
  if (!hud) return;

  const pad = 12 * d;
  g.font = `500 ${11 * d}px "IBM Plex Mono", monospace`;
  g.textBaseline = 'top';
  g.fillStyle = 'rgba(8,8,12,.55)';
  g.fillRect(W - pad - 14 * d, pad + 18 * d, 14 * d, H - 2 * pad - 40 * d);
  const gr = g.createLinearGradient(0, pad + 18 * d, 0, H - pad - 22 * d);
  IB.slice()
    .reverse()
    .forEach(([o, c]) => gr.addColorStop(1 - o, `rgb(${c})`));
  g.fillStyle = gr;
  g.fillRect(W - pad - 11 * d, pad + 21 * d, 8 * d, H - 2 * pad - 46 * d);
  g.fillStyle = '#fff';
  g.textAlign = 'right';
  g.fillText(fmt(s.mx), W - pad, pad);
  g.textBaseline = 'bottom';
  g.fillText(fmt(s.mn), W - pad, H - pad);
  g.textAlign = 'left';
  g.textBaseline = 'top';
  if (W / d > 520) g.fillText(`${w}×${h} · 25 Hz · ε 0,95`, W * 0.5 + pad, pad); // masqué en petit pour ne pas chevaucher la température max

  const x0 = (W - H) / 2;
  const at = (u, v) => (sq ? [x0 + ((u - 0.125) / 0.75) * H, v * H] : [u * W, v * H]);
  const inView = (u) => !sq || (u > 0.135 && u < 0.865);
  const [cx, cy] = at(0.67, 0.42);
  g.strokeStyle = '#fff';
  g.lineWidth = 1.5 * d;
  g.beginPath();
  g.moveTo(cx - 9 * d, cy);
  g.lineTo(cx + 9 * d, cy);
  g.moveTo(cx, cy - 9 * d);
  g.lineTo(cx, cy + 9 * d);
  g.stroke();
  g.fillText(fmt(field(0.67, 0.42)), cx + 8 * d, cy + 8 * d);

  const mark = (u, v, col, lab) => {
    if (!inView(u)) return;
    const [x, y] = at(u, v);
    g.fillStyle = col;
    g.beginPath();
    g.moveTo(x, y - 7 * d);
    g.lineTo(x + 6 * d, y + 4 * d);
    g.lineTo(x - 6 * d, y + 4 * d);
    g.closePath();
    g.fill();
    g.fillStyle = '#fff';
    const right = u > 0.75;
    g.textAlign = right ? 'right' : 'left';
    g.fillText(lab, right ? x - 9 * d : x + 9 * d, y - 4 * d);
    g.textAlign = 'left';
  };
  mark(OUT[0] + 0.02, OUT[1] - 0.03, '#FF3B1F', `Prise ${fmt(field(0.62, 0.77))}`);
  mark(0.86, 0.6, '#3FA9FF', `Humidité ${fmt(field(0.88, 0.6))}`);
}

function drawVisible(canvas) {
  const [W, H] = fit(canvas);
  const g = canvas.getContext('2d');
  const X = (u) => u * W;
  const Y = (v) => v * H;
  let gr = g.createLinearGradient(0, 0, 0, H);
  gr.addColorStop(0, '#D9D6CF');
  gr.addColorStop(1, '#C6C2B9');
  g.fillStyle = gr;
  g.fillRect(0, 0, W, H);
  g.fillStyle = '#BEB9AF';
  g.fillRect(X(0.78), 0, W - X(0.78), H);
  g.fillStyle = '#E6E3DD';
  g.fillRect(0, 0, W, Y(0.03));
  g.fillStyle = '#8B7B66';
  g.fillRect(0, Y(0.9), W, H - Y(0.9));
  g.fillStyle = '#7C6D59';
  for (let i = 0; i < 12; i++) g.fillRect(X(i / 12), Y(0.9), 1.5, H - Y(0.9));
  g.fillStyle = '#F4F3EF';
  g.fillRect(X(WIN[0]), Y(WIN[1]), X(WIN[2] - WIN[0]), Y(WIN[3] - WIN[1]));
  gr = g.createLinearGradient(0, Y(WIN[1]), 0, Y(WIN[3]));
  gr.addColorStop(0, '#9DB7CC');
  gr.addColorStop(1, '#CFDCE4');
  g.fillStyle = gr;
  g.fillRect(X(WIN[0] + 0.016), Y(WIN[1] + 0.02), X(0.13), Y(WIN[3] - WIN[1] - 0.04));
  g.fillRect(X(0.302), Y(WIN[1] + 0.02), X(0.142), Y(WIN[3] - WIN[1] - 0.04));
  g.fillStyle = '#EFEDE8';
  g.fillRect(X(0.1), Y(0.55), X(0.38), Y(0.025));
  g.fillStyle = '#F2F1EE';
  g.fillRect(X(RAD[0]), Y(RAD[1]), X(RAD[2] - RAD[0]), Y(RAD[3] - RAD[1]));
  g.strokeStyle = '#D3D1CC';
  g.lineWidth = 2;
  for (let u = RAD[0] + 0.02; u < RAD[2]; u += 0.02) {
    g.beginPath();
    g.moveTo(X(u), Y(RAD[1] + 0.01));
    g.lineTo(X(u), Y(RAD[3] - 0.01));
    g.stroke();
  }
  g.fillStyle = '#F5F4F1';
  g.fillRect(X(OUT[0]), Y(OUT[1]), X(OUT[2] - OUT[0]), Y(OUT[3] - OUT[1]));
  g.fillStyle = '#9C988F';
  g.beginPath();
  g.arc(X(0.614), Y(0.77), 2.4, 0, 7);
  g.arc(X(0.631), Y(0.77), 2.4, 0, 7);
  g.fill();
  g.fillStyle = '#3F3B36';
  g.fillRect(X(0.62), Y(0.8), 2, Y(0.1));
  g.fillStyle = 'rgba(140,130,110,.18)';
  g.beginPath();
  g.ellipse(X(0.88), Y(0.6), X(0.05), Y(0.07), 0, 0, 7);
  g.fill();
}

/** Redessine à la taille réelle, une fois les polices chargées et à chaque redimensionnement. */
function whenSized(el, draw) {
  let frame = 0;
  const run = () => {
    cancelAnimationFrame(frame);
    frame = requestAnimationFrame(draw);
  };
  const ro = new ResizeObserver(run);
  ro.observe(el);
  document.fonts?.ready.then(run);
  return () => {
    ro.disconnect();
    cancelAnimationFrame(frame);
  };
}

class ExThermalViewer extends HTMLElement {
  connectedCallback() {
    const range = this.querySelector('input[type="range"]');
    this.style.setProperty('--cut', `${range?.value ?? 50}%`);
    range?.addEventListener('input', () => this.style.setProperty('--cut', `${range.value}%`));
    // Réponse au toucher : la poignée réagit dès que le doigt se pose, pas au relâchement
    const press = (on) => () => this.toggleAttribute('data-pressed', on);
    range?.addEventListener('pointerdown', press(true));
    ['pointerup', 'pointercancel', 'lostpointercapture', 'blur'].forEach((t) => range?.addEventListener(t, press(false)));
    // Indice de geste : un aller-retour unique du curseur la première fois que la scène est visible,
    // interrompu dès que l'utilisateur la touche (on repart toujours de la valeur affichée)
    if (range && !matchMedia('(prefers-reduced-motion: reduce)').matches) {
      const home = Number(range.value);
      let raf = 0;
      const stop = () => cancelAnimationFrame(raf);
      range.addEventListener('pointerdown', stop, { once: true });
      range.addEventListener('keydown', stop, { once: true });
      const io = new IntersectionObserver(([e]) => {
        if (!e.isIntersecting) return;
        io.disconnect();
        const targets = [home + 16, home];
        let x = Number(range.value);
        let v = 0;
        let last = 0;
        const step = (t) => {
          const dt = Math.min((t - (last || t)) / 1000, 1 / 30);
          last = t;
          // ressort amorti critique (amortissement 1,0, réponse 0,45 s) : pas de rebond, mouvement continu
          const w = (2 * Math.PI) / 0.45;
          const a = w * w * (targets[0] - x) - 2 * w * v;
          v += a * dt;
          x += v * dt;
          range.value = String(x);
          this.style.setProperty('--cut', `${x}%`);
          if (Math.abs(targets[0] - x) < 0.15 && Math.abs(v) < 0.5) {
            targets.shift();
            if (!targets.length) return;
          }
          raf = requestAnimationFrame(step);
        };
        setTimeout(() => (raf = requestAnimationFrame(step)), 500);
      }, { threshold: 0.6 });
      io.observe(this);
    }
    const visible = this.querySelector('canvas[data-layer="visible"]');
    const thermal = this.querySelector('canvas[data-layer="thermal"]');
    if (visible || thermal) {
      this.cleanup = whenSized(this, () => {
        if (visible) drawVisible(visible);
        if (thermal) drawThermal(thermal, { w: 256, h: 192 });
      });
    }
  }

  disconnectedCallback() {
    this.cleanup?.();
  }
}

class ExResoSim extends HTMLElement {
  connectedCallback() {
    this.canvas = this.querySelector('canvas');
    this.seg = this.querySelector('[data-seg]');
    this.smooth = this.querySelector('input[type="checkbox"]');
    const pressed = this.seg?.querySelector('[aria-pressed="true"]');
    this.reso = { w: Number(pressed?.dataset.w ?? 256), h: Number(pressed?.dataset.h ?? 192) };
    this.seg?.addEventListener('click', (e) => {
      const b = e.target instanceof Element ? e.target.closest('button') : null;
      if (!b) return;
      this.seg.querySelectorAll('button').forEach((x) => x.setAttribute('aria-pressed', String(x === b)));
      this.reso = { w: Number(b.dataset.w), h: Number(b.dataset.h) };
      this.draw();
    });
    this.smooth?.addEventListener('change', () => this.draw());
    // Vraie image thermique (réglage de la section) : chargée une fois, échantillonnée à chaque changement
    if (this.dataset.src) {
      const img = new Image();
      img.onload = () => {
        this.photo = img;
        this.draw();
      };
      img.src = this.dataset.src;
    }
    if (this.canvas) this.cleanup = whenSized(this.canvas, () => this.draw());
  }

  disconnectedCallback() {
    this.cleanup?.();
  }

  /** Échantillonne la photo à la résolution du capteur (recadrage centré), puis l'agrandit au cadre. */
  drawPhoto() {
    const { w, h } = this.reso;
    const [W, H, d] = fit(this.canvas);
    const g = this.canvas.getContext('2d');
    const small = document.createElement('canvas');
    small.width = w;
    small.height = h;
    const sg = small.getContext('2d');
    const img = this.photo;
    const r = Math.max(w / img.naturalWidth, h / img.naturalHeight);
    const sw = w / r;
    const sh = h / r;
    sg.imageSmoothingQuality = 'high';
    sg.drawImage(img, (img.naturalWidth - sw) / 2, (img.naturalHeight - sh) / 2, sw, sh, 0, 0, w, h);
    g.fillStyle = '#0b0b10';
    g.fillRect(0, 0, W, H);
    g.imageSmoothingEnabled = Boolean(this.smooth?.checked);
    g.imageSmoothingQuality = 'high';
    const scale = Math.min(W / w, H / h);
    const dw = w * scale;
    const dh = h * scale;
    g.drawImage(small, (W - dw) / 2, (H - dh) / 2, dw, dh);
    g.font = `500 ${11 * d}px ui-monospace, monospace`;
    g.textBaseline = 'top';
    g.fillStyle = 'rgba(8,8,12,.55)';
    const label = `${w}×${h} · ${(w * h).toLocaleString('fr-FR')} points`;
    const pad = 12 * d;
    const tw = g.measureText(label).width + 16 * d;
    g.fillRect((W - dw) / 2 + pad, (H - dh) / 2 + pad, tw, 24 * d);
    g.fillStyle = '#fff';
    g.fillText(label, (W - dw) / 2 + pad + 8 * d, (H - dh) / 2 + pad + 6 * d);
  }

  draw() {
    if (!this.canvas) return;
    if (this.photo) this.drawPhoto();
    else drawThermal(this.canvas, { ...this.reso, smooth: Boolean(this.smooth?.checked), hud: true });
    const pts = this.reso.w * this.reso.h;
    const set = (k, v) => {
      const el = this.querySelector(`[data-fact="${k}"]`);
      if (el) el.textContent = v;
    };
    set('pts', pts.toLocaleString('fr-FR'));
    set('x', `×${(pts / 9216).toLocaleString('fr-FR', { maximumFractionDigits: 1 })}`);
    set('use', pts < 10000 ? 'Découverte' : pts < 30000 ? 'Polyvalent' : 'Pro');
  }
}

/* Les tuiles sont de vrais liens vers la collection « usage ». Quand la page contient une grille ex-products,
   on filtre sur place (comme la maquette) au lieu de quitter la page. */
class ExUseFilter extends HTMLElement {
  connectedCallback() {
    this.addEventListener('click', (e) => {
      const tile = e.target instanceof Element ? e.target.closest('[data-use]') : null;
      if (!tile) return;
      const grid = document.querySelector('[data-ex-grid]');
      if (!grid) return;
      e.preventDefault();
      const on = tile.getAttribute('aria-current') === 'true';
      this.apply(on ? null : tile.dataset.use, tile.dataset.label, grid);
      const target = grid.closest('[data-ex-products]') ?? grid;
      target.scrollIntoView({ behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth' });
    });
    document.addEventListener('ex:reset-filter', () => {
      const grid = document.querySelector('[data-ex-grid]');
      if (grid) this.apply(null, '', grid);
    });
  }

  apply(id, label, grid) {
    this.querySelectorAll('[data-use]').forEach((b) => b.setAttribute('aria-current', String(b.dataset.use === id)));
    const limit = Number(grid.dataset.limit || 8);
    let n = 0;
    grid.querySelectorAll('[data-uses]').forEach((card, i) => {
      const show = id ? card.dataset.uses.split(' ').includes(id) : i < limit;
      card.hidden = !show;
      if (show) n++;
    });
    const wrap = grid.closest('[data-ex-products]');
    if (!wrap) return;
    const empty = wrap.querySelector('[data-empty]');
    if (empty) empty.hidden = n > 0;
    const state = wrap.querySelector('[data-filter-state]');
    if (state) state.hidden = !id;
    const title = wrap.querySelector('[data-grid-title]');
    if (title) {
      title.dataset.html ??= title.innerHTML; // garde le dégradé du titre d'origine
      if (id) title.textContent = title.dataset.filtered;
      else title.innerHTML = title.dataset.html;
    }
    const lab = wrap.querySelector('[data-filter-label]');
    if (lab && id) lab.textContent = `${n} produit${n > 1 ? 's' : ''} pour : ${label}`;
  }
}

document.addEventListener('click', (e) => {
  if (e.target instanceof Element && e.target.closest('[data-filter-reset]')) {
    document.dispatchEvent(new CustomEvent('ex:reset-filter'));
  }
});

if (!customElements.get('ex-thermal-viewer')) customElements.define('ex-thermal-viewer', ExThermalViewer);
if (!customElements.get('ex-reso-sim')) customElements.define('ex-reso-sim', ExResoSim);
if (!customElements.get('ex-use-filter')) customElements.define('ex-use-filter', ExUseFilter);

/* Livraison estimée : dates en jours ouvrés (lundi–vendredi) à partir d'aujourd'hui. */
class ExEta extends HTMLElement {
  connectedCallback() {
    const fmt = (d) => d.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' });
    const addDays = (n) => {
      const d = new Date();
      let k = 0;
      while (k < n) {
        d.setDate(d.getDate() + 1);
        if (d.getDay() % 6) k++;
      }
      return d;
    };
    const handling = Number(this.dataset.handling || 0);
    const min = Number(this.dataset.min || 0);
    const max = Number(this.dataset.max || 0);
    const set = (k, v) => {
      const el = this.querySelector(`[data-eta="${k}"]`);
      if (el) el.textContent = v;
    };
    set('0', fmt(new Date()));
    set('ship', handling ? `d'ici le ${fmt(addDays(handling))}` : fmt(new Date()));
    if (min && max) set('deliver', `livré du ${fmt(addDays(min))} au ${fmt(addDays(max))}`);
  }
}

if (!customElements.get('ex-eta')) customElements.define('ex-eta', ExEta);

/* ---------- Assistant de choix : besoin → utilisation → budget → 3 recommandations ---------- */
const cardData = (c) => ({
  uses: (c.dataset.uses || '').split(' ').filter(Boolean),
  compat: (c.dataset.compat || '').split(' ').filter(Boolean),
  level: c.dataset.level || '',
  price: Number(c.dataset.price || 0),
  px: Number(c.dataset.px || 0),
  eu: c.dataset.eu === 'true',
});

class ExFinder extends HTMLElement {
  connectedCallback() {
    this.answers = {};
    this.pool = [...this.querySelectorAll('[data-pool] .ex-card')];
    this.addEventListener('click', (e) => {
      const t = e.target instanceof Element ? e.target : null;
      if (!t) return;
      const opt = t.closest('.ex-finder__opt');
      if (opt) {
        const step = Number(opt.closest('[data-step]').dataset.step);
        opt.parentElement.querySelectorAll('.ex-finder__opt').forEach((o) => o.setAttribute('aria-pressed', String(o === opt)));
        this.answers[step] = opt.dataset.value;
        this.go(step + 1);
      } else if (t.closest('[data-back]')) {
        this.go(Number(t.closest('[data-step]').dataset.step) - 1);
      } else if (t.closest('[data-restart]')) {
        this.answers = {};
        this.querySelectorAll('.ex-finder__opt').forEach((o) => o.removeAttribute('aria-pressed'));
        this.go(1);
      }
    });
  }

  go(step) {
    // cohérence spatiale : on avance par la droite, on revient par la gauche
    this.dataset.dir = step >= (this.current || 1) ? 'next' : 'back';
    this.current = step;
    this.querySelectorAll('[data-step]').forEach((el) => (el.hidden = Number(el.dataset.step) !== step));
    this.querySelectorAll('[data-dot]').forEach((d) => {
      const n = Number(d.dataset.dot);
      if (n === step) d.setAttribute('aria-current', 'step');
      else d.removeAttribute('aria-current');
      d.classList.toggle('is-done', n < step);
    });
    if (step === 4) this.recommend();
    const top = this.getBoundingClientRect().top;
    if (top < 0 || top > window.innerHeight * 0.6) {
      this.scrollIntoView({ behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth', block: 'start' });
    }
    const focus = this.querySelector(`[data-step="${step}"] .ex-finder__opt, [data-step="${step}"] [data-result-title]`);
    focus?.focus({ preventScroll: true });
  }

  recommend() {
    const use = this.answers[1];
    const compat = this.answers[2];
    const budget = Number(this.answers[3] || 0);
    const okUse = (d) => use === 'any' || d.uses.includes(use);
    const okCompat = (d) => compat === 'any' || d.compat.includes(compat);
    const okBudget = (d) => !budget || d.price <= budget;
    const all = this.pool.map((c) => ({ c, d: cardData(c) }));
    let list = all.filter(({ d }) => okUse(d) && okCompat(d) && okBudget(d));
    let note = '';
    if (!list.length && budget) {
      list = all.filter(({ d }) => okUse(d) && okCompat(d));
      if (list.length) note = 'Rien dans ce budget pour ce besoin : voici les modèles les plus proches.';
    }
    if (!list.length && compat !== 'any') {
      list = all.filter(({ d }) => okUse(d) && okBudget(d));
      if (!list.length) list = all.filter(({ d }) => okUse(d));
      if (list.length) note = 'Aucun modèle de ce type pour ce besoin : voici les autres solutions adaptées.';
    }
    if (!list.length) {
      list = all;
      note = 'Écrivez-nous en décrivant votre situation : on vous oriente vers le bon appareil.';
    }
    const byPrice = [...list].sort((a, b) => a.d.price - b.d.price);
    const picks = [];
    const add = (item, label) => {
      if (!item) return;
      const found = picks.find((p) => p.item === item);
      if (found) found.labels.push(label);
      else if (picks.length < 3) picks.push({ item, labels: [label] });
    };
    add(byPrice[0], 'Le plus abordable');
    const precise = [...list].filter(({ d }) => d.px > 0).sort((a, b) => b.d.px - a.d.px || a.d.price - b.d.price)[0];
    if (precise) add(precise, 'Le plus précis');
    else add(byPrice[byPrice.length - 1], 'Le plus complet');
    add(byPrice.find((x) => x.d.eu && !picks.some((p) => p.item === x)), 'Expédié depuis l’UE, livré plus vite');
    for (const x of byPrice) if (picks.length < 3 && !picks.some((p) => p.item === x)) add(x, 'Aussi adapté');

    const grid = this.querySelector('[data-result-grid]');
    grid.replaceChildren(
      ...picks.map(({ item, labels }) => {
        const card = item.c.cloneNode(true);
        card.hidden = false;
        const why = document.createElement('p');
        why.className = 'ex-finder__why';
        why.textContent = labels.join(' · ');
        card.querySelector('.ex-card__info')?.prepend(why);
        return card;
      }),
    );
    const n = this.querySelector('[data-result-note]');
    n.hidden = !note;
    n.textContent = note;
    this.querySelector('[data-result-title]').textContent =
      picks.length > 1 ? `Nos ${picks.length} recommandations pour vous` : 'Notre recommandation pour vous';
  }
}

if (!customElements.get('ex-finder')) customElements.define('ex-finder', ExFinder);

/* ---------- Collection : filtres instantanés (ET entre groupes, OU dans un groupe) et tri ---------- */
class ExCollFilter extends HTMLElement {
  connectedCallback() {
    this.grid = this.querySelector('[data-coll-grid]');
    this.cards = [...this.grid.querySelectorAll('.ex-card')];
    this.cards.forEach((c, i) => (c.dataset.order = String(i)));
    this.sel = {};
    // masque les options sans aucun produit dans la collection, puis les groupes vides
    this.querySelectorAll('[data-group]').forEach((g) => {
      let shown = 0;
      g.querySelectorAll('.ex-fchip').forEach((b) => {
        const has = this.cards.some((c) => this.match(cardData(c), g.dataset.group, [b.dataset.value]));
        b.hidden = !has;
        if (has) shown++;
      });
      g.hidden = shown < (g.dataset.group === 'eu' ? 1 : 2);
    });
    this.addEventListener('click', (e) => {
      const t = e.target instanceof Element ? e.target : null;
      const chip = t?.closest('.ex-fchip');
      if (chip) {
        const on = chip.getAttribute('aria-pressed') !== 'true';
        chip.setAttribute('aria-pressed', String(on));
        const g = chip.closest('[data-group]').dataset.group;
        const set = new Set(this.sel[g] || []);
        on ? set.add(chip.dataset.value) : set.delete(chip.dataset.value);
        this.sel[g] = [...set];
        this.apply();
      } else if (t?.closest('[data-reset]')) {
        this.sel = {};
        this.querySelectorAll('.ex-fchip').forEach((b) => b.setAttribute('aria-pressed', 'false'));
        this.apply();
      }
    });
    this.querySelector('[data-sort]')?.addEventListener('change', (e) => this.sort(e.target.value));
  }

  match(d, group, values) {
    if (!values?.length) return true;
    if (group === 'compat') return values.some((v) => d.compat.includes(v));
    if (group === 'level') return values.includes(d.level);
    if (group === 'eu') return d.eu;
    if (group === 'budget') {
      return values.some((v) => {
        const [lo, hi] = v.split('-');
        return d.price >= Number(lo || 0) && (!hi || d.price < Number(hi));
      });
    }
    return true;
  }

  apply() {
    let n = 0;
    this.cards.forEach((c) => {
      const d = cardData(c);
      const ok = Object.entries(this.sel).every(([g, v]) => this.match(d, g, v));
      c.hidden = !ok;
      if (ok) n++;
    });
    const active = Object.values(this.sel).some((v) => v.length);
    this.querySelector('[data-count]').textContent = String(n);
    this.querySelectorAll('[data-reset]').forEach((b) => (b.hidden = !active));
    this.querySelector('[data-empty]').hidden = n > 0;
  }

  sort(mode) {
    const key = {
      'price-asc': (a, b) => cardData(a).price - cardData(b).price,
      'price-desc': (a, b) => cardData(b).price - cardData(a).price,
      'px-desc': (a, b) => cardData(b).px - cardData(a).px || cardData(a).price - cardData(b).price,
    }[mode] || ((a, b) => Number(a.dataset.order) - Number(b.dataset.order));
    const empty = this.querySelector('[data-empty]');
    [...this.cards].sort(key).forEach((c) => this.grid.insertBefore(c, empty));
  }
}

if (!customElements.get('ex-coll-filter')) customElements.define('ex-coll-filter', ExCollFilter);

/* ---------- Jargon expliqué : un « ? » après les termes techniques des fiches et comparatifs ---------- */
const GLOSSARY = [
  [/r[ée]solution (infrarouge )?native|r[ée]solution du capteur|natif$/i, 'Résolution native', 'Le nombre réel de points de mesure du capteur. Plus il est élevé, plus l’image thermique est détaillée. C’est le chiffre à comparer entre deux caméras.'],
  [/image affich[ée]e|affichage am[ée]lior[ée]|super ir|tisr|isr|x³ir/i, 'Image améliorée', 'Une image agrandie et lissée par logiciel pour être plus agréable à regarder. Elle ne mesure pas plus de points que le capteur natif.'],
  [/netd|sensibilit[ée] thermique/i, 'Sensibilité (NETD)', 'Le plus petit écart de température que la caméra distingue. Plus le chiffre est bas, plus elle voit de faibles différences : 50 mK = 0,05 °C.'],
  [/^pr[ée]cision/i, 'Précision', 'L’erreur possible sur la température affichée. ±2 °C suffit largement pour repérer un défaut : ce n’est pas un instrument de laboratoire.'],
  [/fr[ée]quence d.image/i, 'Fréquence d’image', 'Le nombre d’images par seconde. À 25 Hz, l’image reste fluide quand on balaie un mur ou un tableau.'],
  [/champ de vision|angle de vue/i, 'Champ de vision', 'La largeur de la zone vue d’un coup. Grand angle : on voit large de près. Angle étroit : on voit plus loin, plus en détail.'],
  [/[ée]missivit[ée]/i, 'Émissivité', 'Un réglage qui tient compte de la matière visée. La valeur par défaut (0,95) convient aux murs, au bois, au plâtre et aux surfaces mates.'],
  [/bande spectrale/i, 'Bande spectrale', 'Les longueurs d’onde infrarouges captées. 8 à 14 µm est la norme pour mesurer les températures du quotidien.'],
  [/r[ée]solution spatiale|ifov|mrad/i, 'Résolution spatiale', 'La taille du plus petit détail mesurable à distance. Plus le chiffre est petit, plus on voit fin de loin.'],
  [/^ip6[78]$|^ip54$|[ée]tanch[ée]it[ée]|protection/i, 'Indice IP', 'La protection contre la poussière et l’eau. IP54 : poussière et projections. IP67 : immersion temporaire. IP68 : immersion prolongée.'],
  [/otg|branchement/i, 'Branchement OTG', 'Le téléphone alimente l’appareil branché sur sa prise : rien à recharger. Presque tous les téléphones récents le permettent.'],
  [/profondeur de champ|mise au point/i, 'Mise au point', 'La distance à laquelle l’image est nette. En dehors, l’image devient floue.'],
];

let tipSeq = 0;
function addTip(el) {
  if (el.dataset.tip) return;
  const text = el.textContent.trim();
  const g = GLOSSARY.find(([re]) => re.test(text));
  if (!g) return;
  el.dataset.tip = '1';
  const id = `ex-tip-${++tipSeq}`;
  const b = document.createElement('button');
  b.type = 'button';
  b.className = 'ex-tip';
  b.textContent = '?';
  b.setAttribute('aria-label', `Explication : ${g[1]}`);
  b.setAttribute('aria-expanded', 'false');
  b.setAttribute('aria-controls', id);
  const pop = document.createElement('span');
  pop.className = 'ex-tip__pop';
  pop.id = id;
  pop.hidden = true;
  pop.innerHTML = '<b></b><span></span>';
  pop.firstChild.textContent = g[1];
  pop.lastChild.textContent = g[2];
  const wrap = document.createElement('span');
  wrap.className = 'ex-tip-wrap';
  wrap.append(b, pop);
  el.append(wrap);
}

function closeTips(except) {
  document.querySelectorAll('.ex-tip[aria-expanded="true"]').forEach((b) => {
    if (b === except) return;
    b.setAttribute('aria-expanded', 'false');
    document.getElementById(b.getAttribute('aria-controls')).hidden = true;
  });
}

document.addEventListener('click', (e) => {
  const b = e.target instanceof Element ? e.target.closest('.ex-tip') : null;
  if (b) {
    e.preventDefault();
    e.stopPropagation();
    const open = b.getAttribute('aria-expanded') !== 'true';
    closeTips(b);
    b.setAttribute('aria-expanded', String(open));
    const pop = document.getElementById(b.getAttribute('aria-controls'));
    pop.hidden = !open;
    if (open) {
      pop.classList.remove('is-left');
      if (pop.getBoundingClientRect().right > window.innerWidth - 8) pop.classList.add('is-left');
    }
  } else if (!(e.target instanceof Element && e.target.closest('.ex-tip__pop'))) closeTips();
});
document.addEventListener('keydown', (e) => {
  if (e.key === 'Escape') closeTips();
});

function initTips(root = document) {
  root.querySelectorAll('.ex-spec dt, .ex-pdp-head .ex-chip, .ex-table thead th').forEach(addTip);
}
initTips();
document.addEventListener('shopify:section:load', (e) => initTips(e.target));

/* ---------- Header : la séparation n'apparaît que quand du contenu passe dessous ---------- */
{
  const root = document.documentElement;
  const update = (e) => {
    const t = e?.target;
    const y = t && t !== document && t.scrollTop !== undefined ? t.scrollTop : window.scrollY;
    root.toggleAttribute('data-ex-scrolled', y > 4);
  };
  window.addEventListener('scroll', update, { passive: true });
  document.querySelector('.page-wrapper')?.addEventListener('scroll', update, { passive: true });
  update();
}
